/**
 * JUUZ × Loop — Bundle-Transaction-Integration für die eigene Produktseite (PDP)
 * ------------------------------------------------------------------------------
 * Zweck: Wenn ein Kunde auf der JUUZ-Produktseite ein Abo (Abo-Box) in den
 * Warenkorb legt, wird VOR dem cart/add eine Loop-Bundle-Transaction erzeugt
 * und die txnId als `_bundleId` an alle Bundle-Zeilen gehängt.
 * Ergebnis: Das Abo entsteht als echtes Bundle-Abo → Kunde bekommt im Portal
 * die native "Bearbeiten"-Bundle-Ansicht (Sorten + Bracket wechselbar).
 *
 * Einbindung: als Asset ins Theme (z.B. assets/loop-bundle-cart.js) und im
 * Produkt-Template laden. Dann statt des bisherigen cart/add-Aufrufs
 * `JuuzLoopBundle.addToCart(selection)` verwenden (siehe unten).
 */

const JuuzLoopBundle = (() => {
  // ── Stammdaten (aus Read bundle details, Stand 11.08.2026) ──────────────
  const MYSHOPIFY_DOMAIN = "ba4e7b-3f.myshopify.com";
  const BUNDLE_ID = "01KWYMJQ05MRE5BGE1EAAM86H5"; // öffentliche Bundle-ID
  const BUNDLE_NAME = "JUUZ Abo-Box";
  const API_BASE = "https://api.loopsubscriptions.com/storefront/2023-10";

  // Boxgrößen (Dosen → boxSizeId + Bundle-Rabattcode) — Reihenfolge aus API
  const BOX_SIZES = {
    12:  { boxSizeId: "01KWYMJQ071ZJ4ECKE7WDBSK40", discountCode: "LPBNDLx0urf", packs: 4 },
    24:  { boxSizeId: "01KWYMJQ0BT7PBW420F237XNCA", discountCode: "LPBNDLo19av", packs: 8 },
    36:  { boxSizeId: "01KWYMJQ0FRR63NYQ74SBDTM4A", discountCode: "LPBNDLl8qkl", packs: 12 },
    72:  { boxSizeId: "01KWYMJQ0K8ZKX8N2B32G68GZ9", discountCode: "LPBNDLp5lf1", packs: 24 },
    144: { boxSizeId: "01KWYMJQ0QANVMG5Z32VSF6XYC", discountCode: "LPBNDL0hxg8", packs: 48 },
  };

  // Lieferfrequenzen (Selling Plans des Plans "Abo-Box neutral")
  const SELLING_PLANS = {
    "4w": 690415960389, // Alle 4 Wochen
    "6w": 690415993157, // Alle 6 Wochen
    "8w": 690416025925, // Alle 8 Wochen
  };

  // Sorten → Varianten-IDs (Bundle-gemappte Varianten)
  const VARIANTS = {
    berry:     58257715790149, // JUUZ Proteinlimo Berry 3-Pack
    colaLemon: 58257696817477, // JUUZ Proteinlimo Cola Lemon 3-Pack
    lemon:     58257671815493, // JUUZ Proteinlimo Lemon 3-Pack
    orange:    58257619976517, // JUUZ Proteinlimo Orange 3-Pack
  };

  // ── Schritt 1: Bundle-Transaction bei Loop anlegen ──────────────────────
  // selection = { cans: 24, frequency: "8w", flavors: { berry:2, colaLemon:2, lemon:2, orange:2 } }
  // flavors-Werte = Anzahl 3-PACKS je Sorte; Summe muss BOX_SIZES[cans].packs ergeben!
  async function createTransaction(selection) {
    const box = BOX_SIZES[selection.cans];
    if (!box) throw new Error("Unbekannte Boxgröße: " + selection.cans);

    const productVariants = Object.entries(selection.flavors)
      .filter(([, qty]) => qty > 0)
      .map(([flavor, qty]) => ({ shopifyId: VARIANTS[flavor], quantity: qty }));

    const totalPacks = productVariants.reduce((s, v) => s + v.quantity, 0);
    if (totalPacks !== box.packs)
      throw new Error(`Box unvollständig: ${totalPacks}/${box.packs} Packs gewählt`);

    const res = await fetch(
      `${API_BASE}/bundle/${BUNDLE_ID}/transaction?myshopifyDomain=${MYSHOPIFY_DOMAIN}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          boxSizeId: box.boxSizeId,
          sellingPlanShopifyId: SELLING_PLANS[selection.frequency],
          quantity: 1, // WICHTIG: 1 = ein Bundle. NIEMALS die Dosen-/Pack-Zahl!
          productVariants,
        }),
      }
    );
    const json = await res.json();
    if (!json.success) throw new Error("Loop-Transaction fehlgeschlagen: " + json.code);
    // txnId-Feld je nach Response-Envelope:
    return json.data.txnId || json.data.id || json.data.transactionId;
  }

  // ── Schritt 2: Bundle-Rabattcode am Warenkorb registrieren ──────────────
  async function applyBundleDiscount(cans) {
    // Registriert den Code für den Checkout (Shopify-Standardmechanismus)
    await fetch(`/discount/${BOX_SIZES[cans].discountCode}`, { credentials: "same-origin" });
  }

  // ── Schritt 3: Alle Bundle-Zeilen in EINEM cart/add-Aufruf hinzufügen ────
  async function addToCart(selection) {
    const txnId = await createTransaction(selection);

    const items = Object.entries(selection.flavors)
      .filter(([, qty]) => qty > 0)
      .map(([flavor, qty]) => ({
        id: VARIANTS[flavor],
        quantity: qty,                                  // Anzahl 3-PACKS
        selling_plan: SELLING_PLANS[selection.frequency], // NEUTRALER Bundle-Plan (nicht Legacy!)
        properties: {
          _bundleId: txnId,        // ← DER kritische Schlüssel (exakt so!)
          _bundleName: BUNDLE_NAME,
          Dosen: qty * 3,          // wie im bisherigen Fudge-Flow (Cart-Anzeige)
        },
      }));

    await applyBundleDiscount(selection.cans);

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) throw new Error("cart/add fehlgeschlagen: " + res.status);
    return { txnId, cart: await res.json() };
  }

  return { addToCart, createTransaction, BOX_SIZES, SELLING_PLANS, VARIANTS };
})();

/* ── Beispiel-Aufruf (aus eurem bestehenden PDP-Code heraus) ───────────────
   Vorher (heute):  einzelne Sorten-Varianten per cart/add mit selling_plan
   Nachher:
     JuuzLoopBundle.addToCart({
       cans: 24,                    // gewähltes Bracket (12/24/36/72/144)
       frequency: "8w",             // gewählte Lieferfrequenz
       flavors: { berry: 2, colaLemon: 2, lemon: 2, orange: 2 }  // 3-Packs je Sorte
     }).then(() => { window.location.href = "/cart"; })
       .catch(err => { console.error(err); alert("Fehler beim Hinzufügen"); });

   OFFENE PUNKTE vor Live (siehe Anleitung):
   1. txnId-Feldname in der Create-Response verifizieren (einmal im Test loggen).
   2. Prüfen, ob eure PDP dieselben Varianten-IDs nutzt (582577…) — falls die
      PDP andere Varianten (z.B. Nicht-Abo-Varianten 159408…-Produkte) added,
      Mapping anpassen.
   3. Upcharge-Pfand am Checkout bleibt unverändert (läuft parallel, geprüft).
──────────────────────────────────────────────────────────────────────────── */